import React, { useEffect, useState, useMemo, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Stack,
} from "@mui/material";
import { collection, doc, getDoc, onSnapshot } from "firebase/firestore";
import { db } from "../../firebase-config";

function useQueryParams() {
  const location = useLocation();
  return new URLSearchParams(location.search);
}

function formatDateTime(value) {
  if (!value) return "-";
  try {
    const d =
      typeof value?.toDate === "function"
        ? value.toDate()
        : new Date(value);
    if (Number.isNaN(d.getTime())) return "-";
    return d.toLocaleString([], {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "-";
  }
}

function toRgba(input, fallback = "rgba(15, 23, 42, 0.92)") {
  if (!input) return fallback;
  if (typeof input === "string") return input;

  const r = Number.isFinite(Number(input.r)) ? Number(input.r) : 0;
  const g = Number.isFinite(Number(input.g)) ? Number(input.g) : 0;
  const b = Number.isFinite(Number(input.b)) ? Number(input.b) : 0;
  const a = Number.isFinite(Number(input.a)) ? Number(input.a) : 0.6;
  return `rgba(${r}, ${g}, ${b}, ${a})`;
}

const CheckinStatusBoard = () => {
  const query = useQueryParams();
  const navigate = useNavigate();

  // Query first, then sessionStorage fallback (set by CheckinConsole)
  const orgId =
    query.get("org") ||
    sessionStorage.getItem("checkin:orgId") ||
    "";
  const eventId =
    query.get("event") ||
    sessionStorage.getItem("checkin:eventId") ||
    "";

  const [loading, setLoading] = useState(true);
  const [eventInfo, setEventInfo] = useState(null);
  const [rows, setRows] = useState([]);
  const [infoMessage, setInfoMessage] = useState("");
  const [error, setError] = useState("");

  const clientCustomerIdCacheRef = useRef(new Map());

  const hasRequiredParams = orgId && eventId;

  // persist so volunteers can bounce between pages without losing context
  useEffect(() => {
    if (orgId) sessionStorage.setItem("checkin:orgId", orgId);
    if (eventId) sessionStorage.setItem("checkin:eventId", eventId);
  }, [orgId, eventId]);

  useEffect(() => {
    if (!hasRequiredParams) return;

    const loadEvent = async () => {
      try {
        const eventRef = doc(db, "organizations", orgId, "events", eventId);
        const snap = await getDoc(eventRef);
        if (!snap.exists()) return;

        const ev = snap.data() || {};
        const micrositeConfig = ev.micrositeConfig || {};

        const backgroundImageUrl =
          micrositeConfig.backgroundImageUrl || ev.backgroundImageUrl || null;

        const textShadingColor = micrositeConfig.textShadingColor || null;

        setEventInfo({
          eventName: ev.eventName || ev.name || "Event",
          startDate: ev.startDate || null,
          startTime: ev.startTime || null,
          location: ev.location || "",
          eventImage: ev.eventImage || null,
          backgroundImageUrl,
          textShadingColor,
        });
      } catch (err) {
        console.error("[CheckinStatusBoard] loadEvent error:", err);
      }
    };

    loadEvent();
  }, [hasRequiredParams, orgId, eventId]);

  useEffect(() => {
    if (!hasRequiredParams) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError("");
    setInfoMessage("");

    const rsvpsCol = collection(
      db,
      "organizations",
      orgId,
      "events",
      eventId,
      "rsvps"
    );

    const unsubscribe = onSnapshot(
      rsvpsCol,
      (snapshot) => {
        (async () => {
          const baseRows = [];
          const neededClientIds = new Set();

          snapshot.forEach((docSnap) => {
            const data = docSnap.data() || {};
            const attendee = data.attendee || {};
            const checkIn = data.checkIn || {};
            const matchedEntity = data.matchedEntity || {};

            const arrivalAt = checkIn.arrivalAt || null;
            const toysAt = checkIn.toysReceivedAt || null;
            const photosAt = checkIn.photosTakenAt || null;

            // Only show arrived families (same as your current behavior)
            if (!arrivalAt) return;

            const clientDocId =
              matchedEntity?.collection === "clients" && matchedEntity?.id
                ? matchedEntity.id
                : null;

            if (clientDocId && !clientCustomerIdCacheRef.current.has(clientDocId)) {
              neededClientIds.add(clientDocId);
            }

            const lastActivity = photosAt || toysAt || arrivalAt || null;

            baseRows.push({
              id: docSnap.id,
              familyName: attendee.fullName || matchedEntity.fullName || "Unknown family",
              clientDocId,
              customerId: "-",
              arrivalAt,
              toysAt,
              photosAt,
              lastActivity,
            });
          });

          if (neededClientIds.size > 0) {
            await Promise.all(
              Array.from(neededClientIds).map(async (clientDocId) => {
                try {
                  const clientRef = doc(db, "organizations", orgId, "clients", clientDocId);
                  const clientSnap = await getDoc(clientRef);

                  let resolved = "";
                  if (clientSnap.exists()) {
                    const clientData = clientSnap.data() || {};
                    resolved = clientData.customerId || clientData.customerID || "";

                    if (
                      !resolved &&
                      Array.isArray(clientData.children) &&
                      clientData.children.length > 0
                    ) {
                      const firstChild = clientData.children[0] || {};
                      resolved = firstChild.customerId || firstChild.customerID || "";
                    }
                  }

                  clientCustomerIdCacheRef.current.set(clientDocId, resolved || "");
                } catch (e) {
                  console.warn(
                    `[CheckinStatusBoard] Failed client lookup for ${clientDocId}:`,
                    e?.message || e
                  );
                  clientCustomerIdCacheRef.current.set(clientDocId, "");
                }
              })
            );
          }

          const nextRows = baseRows.map((r) => {
            if (!r.clientDocId) return r;
            const cached = clientCustomerIdCacheRef.current.get(r.clientDocId);
            return { ...r, customerId: cached ? String(cached) : "-" };
          });

          nextRows.sort((a, b) => {
            const getMillis = (ts) =>
              typeof ts?.toMillis === "function"
                ? ts.toMillis()
                : new Date(ts || 0).getTime();
            return getMillis(b.arrivalAt) - getMillis(a.arrivalAt);
          });

          setRows(nextRows);

          if (nextRows.length === 0) {
            setInfoMessage(
              "No families have arrived yet. This board updates in real time as guests check in."
            );
          } else {
            setInfoMessage(
              "Board updates in real time as additional families arrive or complete steps."
            );
          }

          setLoading(false);
        })().catch((err) => {
          console.error("[CheckinStatusBoard] snapshot processing error:", err);
          setError("Unable to process RSVP updates.");
          setLoading(false);
        });
      },
      (err) => {
        console.error("[CheckinStatusBoard] onSnapshot error:", err);
        setError("Unable to load RSVPs for this event.");
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [hasRequiredParams, orgId, eventId]);

  const arrivedCount = useMemo(() => rows.length, [rows]);
  const paperBg = useMemo(
    () => toRgba(eventInfo?.textShadingColor, "rgba(2, 6, 23, 0.92)"),
    [eventInfo?.textShadingColor]
  );

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        justifyContent: "center",
        p: { xs: 1, sm: 2, md: 4 },
        position: "relative",
        overflow: "hidden",
        backgroundColor: "#020617",
        ...(eventInfo?.backgroundImageUrl && {
          backgroundImage: `url(${eventInfo.backgroundImageUrl})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
        }),
      }}
    >
      {eventInfo?.backgroundImageUrl && (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            background:
              "linear-gradient(to bottom, rgba(0,0,0,0.55), rgba(0,0,0,0.80))",
            zIndex: 0,
          }}
        />
      )}

      <Card
        sx={{
          width: "100%",
          maxWidth: 960,
          bgcolor: "transparent",
          color: "#e5e7eb",
          boxShadow: 6,
          zIndex: 1,
          borderRadius: 3,
          overflow: "hidden",
        }}
      >
        {eventInfo?.eventImage && (
          <Box
            sx={{
              width: "100%",
              display: "flex",
              justifyContent: "center",
              backgroundColor: "rgba(0,0,0,0.25)",
            }}
          >
            <Box
              sx={{
                width: "100%",
                maxWidth: 960,
                aspectRatio: "3 / 1",
                backgroundImage: `url(${eventInfo.eventImage})`,
                backgroundSize: "contain",
                backgroundRepeat: "no-repeat",
                backgroundPosition: "center",
              }}
            />
          </Box>
        )}

        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 600, mb: 0.5 }}>
                {eventInfo?.eventName || "Event Check-in Board"}
              </Typography>

              {eventInfo && (
                <Typography variant="body2" sx={{ color: "#9ca3af" }}>
                  {eventInfo.startDate
                    ? formatDateTime(
                        eventInfo.startDate?.toDate
                          ? eventInfo.startDate.toDate()
                          : eventInfo.startDate
                      )
                    : ""}
                  {eventInfo.location ? ` • ${eventInfo.location}` : ""}
                </Typography>
              )}
            </Box>

            <Stack direction="row" spacing={1}>
              <Button
                variant="contained"
                onClick={() =>
                  navigate(
                    `/checkin/console?org=${encodeURIComponent(orgId)}&event=${encodeURIComponent(eventId)}`
                  )
                }
                disabled={!hasRequiredParams}
              >
                Open Scanner
              </Button>

              <Button
                variant="outlined"
                onClick={() =>
                  navigate(
                    `/checkin/status?org=${encodeURIComponent(orgId)}&event=${encodeURIComponent(eventId)}`
                  )
                }
                disabled={!hasRequiredParams}
              >
                Refresh Link
              </Button>
            </Stack>
          </Stack>

          {!hasRequiredParams && (
            <Alert severity="error" sx={{ mt: 2 }}>
              Missing event parameters. Include <b>org</b> and <b>event</b> in the URL,
              or open this board from the Check-in Console.
            </Alert>
          )}

          {loading && hasRequiredParams && (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress />
            </Box>
          )}

          {!loading && error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}

          {!loading && !error && hasRequiredParams && (
            <>
              {infoMessage && (
                <Alert
                  severity="info"
                  sx={{
                    mt: 2,
                    mb: 2,
                    bgcolor: "rgba(15, 23, 42, 0.65)",
                    color: "#e5e7eb",
                    "& .MuiAlert-icon": { color: "#38bdf8" },
                  }}
                >
                  {infoMessage}{" "}
                  {arrivedCount > 0 && (
                    <strong style={{ marginLeft: 4 }}>
                      ({arrivedCount} family{arrivedCount === 1 ? "" : "ies"} arrived)
                    </strong>
                  )}
                </Alert>
              )}

              <TableContainer
                component={Paper}
                sx={{
                  maxHeight: "70vh",
                  bgcolor: paperBg,
                  borderRadius: 2,
                  overflow: "auto",
                  backdropFilter: "blur(6px)",
                }}
              >
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow
                      sx={{
                        "& th": {
                          backgroundColor: "rgba(2, 6, 23, 0.95)",
                          color: "#e5e7eb",
                          borderBottom: "1px solid rgba(255,255,255,0.08)",
                          fontWeight: 600,
                          fontSize: 12,
                          textTransform: "uppercase",
                          letterSpacing: "0.05em",
                        },
                      }}
                    >
                      <TableCell>Family</TableCell>
                      <TableCell>Customer ID</TableCell>
                      <TableCell align="center">Arrived</TableCell>
                      <TableCell align="center">Toys</TableCell>
                      <TableCell align="center">Photos</TableCell>
                      <TableCell>Last Activity</TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {rows.map((row) => {
                      const arrived = !!row.arrivalAt;
                      const toysDone = !!row.toysAt;
                      const photosDone = !!row.photosAt;

                      return (
                        <TableRow
                          key={row.id}
                          sx={{
                            "& td": {
                              borderBottom: "1px solid rgba(255,255,255,0.06)",
                              color: "#e5e7eb",
                              fontSize: 13,
                            },
                            "&:hover": {
                              backgroundColor: "rgba(255,255,255,0.04)",
                            },
                          }}
                        >
                          <TableCell>{row.familyName}</TableCell>
                          <TableCell>{row.customerId || "-"}</TableCell>

                          <TableCell align="center">
                            <Chip
                              size="small"
                              label={arrived ? "Yes" : "No"}
                              color={arrived ? "success" : "default"}
                              variant={arrived ? "filled" : "outlined"}
                            />
                          </TableCell>

                          <TableCell align="center">
                            <Chip
                              size="small"
                              label={toysDone ? "Yes" : "No"}
                              color={toysDone ? "success" : "default"}
                              variant={toysDone ? "filled" : "outlined"}
                            />
                          </TableCell>

                          <TableCell align="center">
                            <Chip
                              size="small"
                              label={photosDone ? "Yes" : "No"}
                              color={photosDone ? "success" : "default"}
                              variant={photosDone ? "filled" : "outlined"}
                            />
                          </TableCell>

                          <TableCell>
                            {row.lastActivity ? formatDateTime(row.lastActivity) : "-"}
                          </TableCell>
                        </TableRow>
                      );
                    })}

                    {rows.length === 0 && (
                      <TableRow>
                        <TableCell
                          colSpan={6}
                          align="center"
                          sx={{
                            py: 4,
                            color: "#cbd5e1",
                            borderBottom: "none",
                          }}
                        >
                          No arrivals yet. Families will appear here as they are checked in.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default CheckinStatusBoard;